// Custom (admin-built) QC checks — the non-SYS side of the dynamic engine.
// The three seeded system templates each own a run-scoped tab on /qc; every
// other template's runs render on the Checks tab, grouped per template.
import { normaliseVariety } from '@/lib/qcSpec'

// System templates are seeded with a SYS_ code prefix (migs 177/828/1038) and
// future seeds keep the convention (e.g. the planned SYS_SAMPLING). Classify
// by prefix, not an enumerated list, so a new system seed is excluded from
// the custom-checks surfaces by construction.
export function isSystemTemplateCode(code: string): boolean {
  return code.startsWith('SYS_')
}

// PostgREST `not in` wire filter (values quoted — see Deliveries.tsx status
// filter). Bandwidth only: LIKE can't safely express the prefix (unescaped
// `_` is a wildcard), so the wire list enumerates today's codes and callers
// re-filter with isSystemTemplateCode so correctness never depends on it.
export const QC_SYSTEM_CODES_NOT_IN = '("SYS_FULL_BOX","SYS_PACKER_PROBLEM","SYS_TITRATION")'

export interface TemplateRunLike {
  template_code: string
  template_name: string
}

export interface TemplateRunGroup<T extends TemplateRunLike> {
  code: string
  name: string
  runs: T[]
}

// Groups runs per template. Incoming order is preserved inside each group
// and decides group order by first appearance — runs arrive newest-first,
// so the template checked most recently sits on top.
export function groupRunsByTemplate<T extends TemplateRunLike>(runs: T[]): TemplateRunGroup<T>[] {
  const byCode = new Map<string, TemplateRunGroup<T>>()
  for (const r of runs) {
    const g = byCode.get(r.template_code)
    if (g) g.runs.push(r)
    else byCode.set(r.template_code, { code: r.template_code, name: r.template_name, runs: [r] })
  }
  return [...byCode.values()]
}

// A persisted filter can name a template with no runs on this run (deleted
// since, or a different run than last time). Resolve it to null so the list
// shows everything AND the "All" chip reads as active — a stale filter must
// never leave the chip row with nothing highlighted.
export function resolveTemplateFilter<T extends TemplateRunLike>(
  groups: TemplateRunGroup<T>[],
  persisted: string | null,
): string | null {
  return persisted != null && groups.some(g => g.code === persisted) ? persisted : null
}

// The templates that apply to a given run — the New Check picker's scoping,
// extracted so the Checks-tab visibility can promise only what the picker
// will actually offer. Variety and commodity must each be blank or match
// (size/grade are per-carton, never a filter); the titration / packer system
// templates are hidden on flows without those lanes. Fails OPEN when the
// run's commodity can't be resolved (un-canonicalised or NULL runs.commodity,
// or the catalog still loading): showing a template that doesn't apply is a
// recoverable annoyance, hiding the only box check on the line is a work
// stoppage.
export interface TemplateScopeLike {
  code: string
  variety: string | null
  commodity_id: string | null
}

export interface TemplateRunScopeOpts {
  runVariety: string | null
  runCommodityId: string | null
  hasTitrationQc: boolean
  hasPackerQc: boolean
}

export function templatesForRun<T extends TemplateScopeLike>(
  templates: T[],
  opts: TemplateRunScopeOpts,
): T[] {
  const runVariety = normaliseVariety(opts.runVariety)
  // One early-return per scoping rule; each rule is pinned by a vitest case.
  // fallow-ignore-next-line complexity
  return templates.filter(t => {
    if (t.code === 'SYS_TITRATION' && !opts.hasTitrationQc) return false
    if (t.code === 'SYS_PACKER_PROBLEM' && !opts.hasPackerQc) return false
    if (t.variety && normaliseVariety(t.variety) !== runVariety) return false
    if (t.commodity_id && opts.runCommodityId && t.commodity_id !== opts.runCommodityId) return false
    return true
  })
}

// The Checks-tab visibility set: templates that apply to the run AND are
// admin-built. Composition kept here so both rules stay under test.
export function customTemplatesForRun<T extends TemplateScopeLike>(
  templates: T[],
  opts: TemplateRunScopeOpts,
): T[] {
  return templatesForRun(templates, opts).filter(t => !isSystemTemplateCode(t.code))
}
